import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NIKO_API_URL } from '../tokens/api.tokens';
import { Tenant } from '../models/tenant.model';
import { Role } from '../models/role.model';
import { Permission } from '../models/permission.model';
import { ApiKey, ApiKeyPermissionEntry } from '../models/api-key.model';
import { PaginatedResult } from '../models/paginated-result.model';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(NIKO_API_URL);

  // ── Tenants ──────────────────────────────────────────────────────────────────

  getTenants(page = 1, limit = 20): Observable<PaginatedResult<Tenant>> {
    return this.http.get<PaginatedResult<Tenant>>(`${this.base}/tenants`, {
      params: { page: page.toString(), limit: limit.toString() },
    });
  }

  getAllTenants(): Observable<Tenant[]> {
    return this.getTenants(1, 100).pipe(map((r) => r.data));
  }

  getActiveTenants(): Observable<Tenant[]> {
    return this.http.get<Tenant[]>(`${this.base}/tenants/active`);
  }

  createTenant(payload: Partial<Tenant>): Observable<Tenant> {
    return this.http.post<Tenant>(`${this.base}/tenants`, payload);
  }

  updateTenant(id: string, payload: Partial<Tenant>): Observable<Tenant> {
    return this.http.patch<Tenant>(`${this.base}/tenants/${id}`, payload);
  }

  // ── Roles ─────────────────────────────────────────────────────────────────────

  getRoles(page = 1, limit = 20, tenantId?: string): Observable<PaginatedResult<Role>> {
    const params: Record<string, string> = { page: page.toString(), limit: limit.toString() };
    if (tenantId) params['tenantId'] = tenantId;
    return this.http.get<PaginatedResult<Role>>(`${this.base}/roles`, { params });
  }

  createRole(name: string, tenantId?: string, description?: string): Observable<Role> {
    return this.http.post<Role>(`${this.base}/roles`, { name, tenantId, description });
  }

  updateRole(id: string, payload: { name: string; description?: string; tenantId?: string }): Observable<Role> {
    return this.http.patch<Role>(`${this.base}/roles/${id}`, payload);
  }

  cloneRole(sourceId: string, tenantId: string): Observable<Role> {
    return this.http.post<Role>(`${this.base}/roles/${sourceId}/clone`, { tenantId });
  }

  // ── Permissions CRUD ─────────────────────────────────────────────────────────

  getPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${this.base}/permissions`);
  }

  createPermission(name: string, description?: string): Observable<Permission> {
    return this.http.post<Permission>(`${this.base}/permissions`, { name, description });
  }

  updatePermission(id: string, name: string, description?: string): Observable<Permission> {
    return this.http.patch<Permission>(`${this.base}/permissions/${id}`, { name, description });
  }

  deletePermission(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/permissions/${id}`);
  }

  // ── Permission associations ───────────────────────────────────────────────────

  getRolePermissions(roleId: string): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${this.base}/roles/${roleId}/permissions`);
  }

  assignRolePermissions(roleId: string, permissionIds: string[]): Observable<void> {
    return this.http.patch<void>(`${this.base}/roles/${roleId}/permissions`, { permissionIds });
  }

  getApiKeyPermissions(apiKeyId: string): Observable<ApiKeyPermissionEntry[]> {
    return this.http.get<ApiKeyPermissionEntry[]>(`${this.base}/permissions/api-keys/${apiKeyId}`);
  }

  assignApiKeyPermissions(id: string, permissionIds: string[]): Observable<void> {
    return this.http.patch<void>(`${this.base}/permissions/api-keys/${id}`, { permissionIds });
  }

  // ── API Keys ──────────────────────────────────────────────────────────────────

  getApiKeys(): Observable<ApiKey[]> {
    return this.http.get<ApiKey[]>(`${this.base}/api-keys`);
  }

  getApiKey(id: string): Observable<ApiKey> {
    return this.http.get<ApiKey>(`${this.base}/api-keys/${id}`);
  }

  createApiKey(payload: {
    name: string;
    tenantId: string;
    expiresAt?: string;
    permissionIds: string[];
  }): Observable<{ apiKey: ApiKey; rawKey: string }> {
    return this.http.post<{ apiKey: ApiKey; rawKey: string }>(`${this.base}/api-keys`, payload);
  }

  revokeApiKey(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/api-keys/${id}`);
  }

  // ── Auth session ──────────────────────────────────────────────────────────────

  selectTenantSession(tenantId: string): Observable<{ accessToken: string; tenant: Tenant }> {
    return this.http.post<{ accessToken: string; tenant: Tenant }>(
      `${this.base}/auth/select-tenant`,
      { tenantId },
    );
  }
}
